/**
 * #2101 — office hours in the browser, as the chat service decides them.
 *
 * ══ WHY THIS FILE EXISTS, AND WHY IT IS A PORT RATHER THAN A SECOND OPINION ══
 *
 * The authority is cs3-chat's `App\Domain\Chat\OfficeHours` and
 * `OutsideHoursRules`, and it stays the authority: a post the browser allows is
 * still refused by the server with a 403, and `chatPanel`'s `sendLive` shows
 * that. What the browser needs is to agree with it, because #2101 is about
 * showing the state BEFORE someone types a message and has it refused.
 *
 * So every rule below is a deliberate, line-for-line port of those two classes,
 * and the places they could drift are called out. Where the two disagree the
 * server wins and the panel is wrong on screen — which is the failure mode to
 * design against, not a reason to put a looser rule here.
 *
 * ⚠ THE PREVIEW'S `isOutsideHours` IS NOT THIS, AND COULD NOT BE REUSED.
 * `chatPreview.js` reads `now.getDay()` and `now.getHours()` — the BROWSER's
 * local clock — and ignores `hours.tz` entirely. That is fine against invented
 * data reviewed by someone sitting in the client's own timezone, and wrong for
 * a real client: a dispatcher in Vancouver looking at a New York local's chat
 * would be told it is closed for three hours of every working afternoon, and
 * told it is open for three hours after the office shut. The preview keeps its
 * version, untouched; a live build uses this one.
 *
 * ⚠ IT TAKES THE API'S SHAPE, NOT THE SETTINGS SCREEN'S. `chatSettings.toScreen`
 * exists for Configuration → Chat and is lossy in two ways that matter here: it
 * re-keys the days into a Sunday-first ARRAY (so `Date#getDay()` can index it),
 * and it drops `outside_hours.people` because the screen cannot resolve a chat
 * uid to a name. The panel does not need a name — it needs to know whether its
 * OWN uid is named — so reading the API's own shape keeps the one fact the
 * other shape throws away, and avoids a second lossy hop.
 */

/**
 * ⚠ US EASTERN, NOT UTC, AND NOT THE BROWSER'S ZONE. This is
 * `OfficeHours::FALLBACK_TZ`, and it is what the server will use for a client
 * whose timezone is empty or unusable — so a browser that fell back to anything
 * else would disagree with the server on exactly the clients that have no
 * timezone set. (Note `chatSettings.toScreen` falls back to 'UTC' for the
 * SCREEN, which is a display default for a field the CA is about to pick; it is
 * not the evaluation default and must not be borrowed as one.)
 */
export const FALLBACK_TZ = 'America/New_York';

/**
 * #2106 — THE STOCK SENTENCE, PORTED. cs3-chat
 * `App\Domain\Chat\PostRefusal::OutsideOfficeHours->message()`, which
 * `OutsideHoursRules::displayMessage()` answers with when the Client Admin has
 * written no wording of their own.
 *
 * ⚠ IT IS HERE TO BE RECOGNISED, NOT TO BE RENDERED. The panel has its own
 * heading for the closed state and names the switch that closed it; this
 * sentence is what the SERVER sends in place of a client message, and the only
 * thing the browser does with it is notice that it is not a client message —
 * see `clientClosedMessage`. (`chatSettings.js` spells it again for the
 * configuration screen's placeholder, because both modules must stay
 * import-free to be testable under node; a test asserts the two agree.)
 */
export const DEFAULT_CLOSED_MESSAGE = 'Chat is read-only outside your office hours.';

/** Monday-first, as `OfficeHours::DAYS` has it and as the schedule is drawn. */
export const DAYS = [
    { key: 'mon', short: 'Mon', label: 'Monday' },
    { key: 'tue', short: 'Tue', label: 'Tuesday' },
    { key: 'wed', short: 'Wed', label: 'Wednesday' },
    { key: 'thu', short: 'Thu', label: 'Thursday' },
    { key: 'fri', short: 'Fri', label: 'Friday' },
    { key: 'sat', short: 'Sat', label: 'Saturday' },
    { key: 'sun', short: 'Sun', label: 'Sunday' },
];

const DAY_KEYS = DAYS.map((d) => d.key);

/** `OfficeHours::time()` — anything that is not "HH:MM" becomes "00:00". */
function timeOrZero(raw) {
    return (typeof raw === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(raw)) ? raw : '00:00';
}

function minutesOf(hhmm) {
    const h = parseInt(hhmm.slice(0, 2), 10);
    const m = parseInt(hhmm.slice(3, 5), 10);

    return (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
}

/**
 * Is this a timezone this browser can actually use?
 *
 * `OfficeHours::tz()` falls back rather than throwing, for the stated reason
 * that "a bad tz string must not make a client's chat unpostable" — the same
 * applies here, where a throw would take the whole panel down.
 */
export function usableTz(candidate) {
    if (typeof candidate !== 'string' || candidate === '') return FALLBACK_TZ;

    try {
        // Constructing the formatter is the only reliable validity test; an
        // unknown IANA name throws RangeError.
        new Intl.DateTimeFormat('en-US', { timeZone: candidate });
    } catch (e) {
        return FALLBACK_TZ;
    }

    return candidate;
}

/**
 * The API's `office_hours` → the shape the rules below read.
 *
 * Mirrors `OfficeHours::fromArray`: absent or `enabled: false` is ALWAYS OPEN,
 * every day key is filled in, and a missing or malformed time becomes "00:00"
 * (which `windowOf` then reads as closed).
 *
 * ⚠ ALWAYS OPEN, NOT ALWAYS CLOSED, for a client with no schedule. The server
 * says why: "A client who has never touched the schedule has not asked for a
 * restriction, and defaulting to closed would silently make every member of
 * every such client read-only the moment this ships."
 */
export function officeHours(raw) {
    const r = (raw && typeof raw === 'object') ? raw : {};
    if (r.enabled !== true) {
        return { enabled: false, tz: FALLBACK_TZ, days: {} };
    }

    const stored = (r.days && typeof r.days === 'object') ? r.days : {};
    const days = {};
    DAY_KEYS.forEach((key) => {
        const one = stored[key] || {};
        days[key] = {
            open: one.open === true,
            from: timeOrZero(one.from),
            to: timeOrZero(one.to),
        };
    });

    return { enabled: true, tz: usableTz(r.tz), days };
}

/**
 * The day's window as [from, to] in minutes, or null when the day is closed or
 * its window is invalid.
 *
 * `OfficeHours::windowOf`, including the part that is easy to get wrong:
 * **`to` at or before `from` reads as CLOSED, never as "all day" and never as
 * "until tomorrow".** That covers the 00:00–00:00 a half-filled form leaves
 * behind and the 22:00–02:00 somebody meant as an overnight shift. Overnight
 * windows are not supported by the schedule model, and the server refuses to
 * guess; so does this.
 *
 * ⚠ AND IT IS USED BY BOTH `isOpenNow` AND `nextOpenLabel`, which is the point
 * of having it. The preview's `nextOpenLabel` tests `day.open` alone, so with an
 * invalid window it advertises "reopens Mon 22:00" for a day the server treats
 * as closed all day — the panel promising a time that never comes.
 */
function windowOf(day) {
    if (!day || !day.open) return null;

    const from = minutesOf(day.from);
    const to = minutesOf(day.to);

    return to <= from ? null : [from, to];
}

/**
 * Where the client's own wall clock is right now: its weekday key and the
 * minutes since its local midnight.
 *
 * `Intl.DateTimeFormat` with an explicit `timeZone` is how a browser does this
 * without a timezone library, and it is correct across DST because it resolves
 * the real zone rather than an offset.
 *
 * ⚠ `hourCycle: 'h23'`, NOT `hour12: false`. With `hour12: false` some engines
 * render midnight as "24", which would put the whole first hour of the day at
 * 1440+ minutes and read as closed on every schedule. Clamped below as well,
 * because being wrong about midnight is not worth trusting one option for.
 */
export function clientLocalNow(tz, now) {
    const when = (now instanceof Date) ? now : new Date();
    const zone = usableTz(tz);

    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone: zone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        }).formatToParts(when);
    } catch (e) {
        // Should be unreachable: usableTz() has already built a formatter for
        // this zone. Falls back to the browser's own clock rather than throwing,
        // because a panel that cannot tell the time must still render.
        return {
            tz: zone,
            dayKey: DAY_KEYS[(when.getDay() + 6) % 7],
            minutes: when.getHours() * 60 + when.getMinutes(),
            exact: false,
        };
    }

    const get = (type) => {
        const hit = parts.find((p) => p.type === type);

        return hit ? hit.value : '';
    };

    const hour = parseInt(get('hour'), 10);
    const minute = parseInt(get('minute'), 10);

    return {
        tz: zone,
        dayKey: String(get('weekday')).toLowerCase().slice(0, 3),
        minutes: ((isNaN(hour) ? 0 : hour) % 24) * 60 + (isNaN(minute) ? 0 : minute),
        exact: true,
    };
}

/**
 * Is the client inside its posting window right now?
 *
 * `OfficeHours::isOpenAt`, half-open at the top: at the closing minute the
 * client is shut, because "chat open to 18:00 that still accepts a post at
 * 18:00:59 is not what 18:00 means".
 */
export function isOpenNow(hours, now) {
    const h = hours || {};
    if (h.enabled !== true) return true;

    const local = clientLocalNow(h.tz, now);
    const window = windowOf((h.days || {})[local.dayKey]);
    if (window === null) return false;

    return local.minutes >= window[0] && local.minutes < window[1];
}

/**
 * "Mon 08:00" — when posting reopens, or null.
 *
 * `OfficeHours::nextOpenAt`, reduced to a LABEL on purpose. The server returns a
 * timestamp; the panel only ever renders "reopens …", and computing a real
 * instant in another timezone from a browser means re-implementing offset
 * arithmetic across DST for a string. The weekday walk is the same, and so is
 * the "today, but already past opening" skip.
 *
 * Returns null when the schedule has no usable window at all, which is why the
 * composer's banner reads "Chat is read-only outside office hours" with no
 * promise attached in that case.
 */
export function nextOpenLabel(hours, now) {
    const h = hours || {};
    if (h.enabled !== true) return null;
    if (isOpenNow(h, now)) return null;

    const local = clientLocalNow(h.tz, now);
    const start = DAY_KEYS.indexOf(local.dayKey);
    if (start < 0) return null;

    for (let ahead = 0; ahead <= 7; ahead++) {
        const day = DAYS[(start + ahead) % 7];
        const window = windowOf((h.days || {})[day.key]);
        if (window === null) continue;
        // Today and already past opening: the window has closed, not reopened.
        if (ahead === 0 && window[0] <= local.minutes) continue;

        return (ahead === 0 ? 'today ' : day.short + ' ') + fmt(window[0]);
    }

    return null;
}

function fmt(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;

    return (h < 10 ? '0' : '') + h + ':' + (m < 10 ? '0' : '') + m;
}

/**
 * May this person post while the client is closed?
 *
 * `OutsideHoursRules::allows`, and the precedence runs BOTH WAYS:
 *
 *   CS staff           always — never restricted by a client's preferences
 *   named `false`      refused, even though their role is allowed
 *   named `true`       allowed, even though their role is not
 *   otherwise          the role switch, DEFAULT FALSE
 *
 * ⚠ THE DEFAULT FOR AN UNLISTED ROLE IS FALSE. The restrictive default is the
 * server's and it is deliberate: "the schedule exists to close posting, and a
 * role the CA has not named is one they have not exempted". A permissive default
 * here would show an open composer to the one group the feature is for.
 *
 * ⚠ AND THE PANEL CAN HONOUR THE NAMED LIST EVEN THOUGH THE SETTINGS SCREEN
 * CANNOT. `outside_hours.people` is keyed by chat uid with no name beside it,
 * which is why Configuration → Chat hides the per-person card (#2099). Asking
 * "is MY uid in there" needs no resolver, and the session knows the uid.
 *
 * @param {object} outsideRaw the API's `outside_hours`
 * @param {string} uid        this user's chat uid (the handoff's `sub`)
 * @param {string} role       their chat role, from the session — never a guess
 */
export function allowedOutsideHours(outsideRaw, uid, role) {
    if (role === 'cs_admin' || role === 'cs_support') return true;

    const o = (outsideRaw && typeof outsideRaw === 'object') ? outsideRaw : {};

    /*
     * ⚠ #2106 — THE SERVER'S OWN ANSWER, WHEN IT SENT ONE.
     *
     * cs3-chat #28's narrow read (`ClientSettingsAdmin::presentHours`, what a
     * Member, Power User, Steward or Employer now gets) carries `may_post`:
     * `OutsideHoursRules::allows($actor)` evaluated for THIS person, by the
     * class that will also decide the 403. It also filters `roles` and `people`
     * down to that person's own entries — deliberately, so the derivation below
     * kept working until this line existed — but the filtered maps are the
     * answer re-stated, not the answer itself.
     *
     * So: read it when it is there. A disagreement between `may_post` and the
     * maps can only mean the server changed its mind about precedence, and the
     * composer should then match the refusal the post would get rather than a
     * rule this file reconstructed.
     *
     * Absent for the CA / CS-staff FULL read (`present`), which sends every
     * role switch and every named person instead — that is what the derivation
     * below is for, and it stays the #2101 port, line for line.
     */
    if (typeof o.may_post === 'boolean') return o.may_post;

    const people = (o.people && typeof o.people === 'object') ? o.people : {};

    if (typeof uid === 'string' && uid !== '' && Object.prototype.hasOwnProperty.call(people, uid)) {
        return people[uid] === true;
    }

    const roles = (o.roles && typeof o.roles === 'object') ? o.roles : {};

    return roles[role] === true;
}

/**
 * The whole question the composer asks: may this person post right now, as far
 * as OFFICE HOURS are concerned?
 *
 * ⚠ OFFICE HOURS ONLY. Archived channels and a job channel's own 24-hour window
 * are separate refusals with their own banners, decided in the panel from the
 * channel the server sent — running them together would tell the reader neither
 * (the remedy for one is "wait for the call", for the other "ask your CA").
 * `ChatPolicy::refusalToPost` orders them the same way, and deliberately checks
 * the schedule LAST.
 */
export function mayPostInHours(settings, uid, role, now) {
    const s = settings || {};
    const hours = officeHours(s.office_hours);

    if (isOpenNow(hours, now)) return true;

    return allowedOutsideHours(s.outside_hours, uid, role);
}

/**
 * #2106 — the client's OWN closed wording, or '' when the Client Admin has
 * written none.
 *
 * Ed, 2026-10-21: "client chat box that speaks to users blocked by hours should
 * display a client defined message."
 *
 * ⚠ '' MEANS "THE CA WROTE NOTHING", AND THAT CANNOT BE READ OFF EMPTINESS.
 * `presentHours` RESOLVES the default server-side, so a Member's read carries
 * the stock sentence rather than an empty string (`displayMessage()`), and the
 * CA's full read carries the message AS STORED plus `default_message` beside
 * it. Either way the question "is this the client speaking, or is this our
 * fallback?" is answered by comparing against the default — the server's copy
 * when it sent one, the ported constant otherwise.
 *
 * It matters because the panel already has a heading for the closed state and a
 * line naming the switch that closed it. Rendering the fallback as if it were
 * the client's words would print the heading twice and drop the explanation.
 *
 * @param {object} settings the API's settings object (either read)
 * @returns {string} the client's wording, trimmed, line breaks intact; '' for none
 */
export function clientClosedMessage(settings) {
    const o = ((settings || {}).outside_hours && typeof settings.outside_hours === 'object')
        ? settings.outside_hours : {};
    const message = typeof o.message === 'string' ? o.message.trim() : '';
    if (message === '') return '';

    // The server's own copy of the fallback, when this read carried one.
    const fallback = typeof o.default_message === 'string' ? o.default_message.trim() : '';
    if (message === fallback || message === DEFAULT_CLOSED_MESSAGE) return '';

    return message;
}

/**
 * Everything the top-of-box strip needs, in one object, so the template has no
 * logic in it.
 *
 * The strip is ALWAYS VISIBLE in a live build (#2101 item 1), including when the
 * client has no schedule — "Open 24 hours" is a fact a crew wants as much as a
 * closing time, and a strip that appears only on closed days would read as the
 * panel breaking.
 *
 * @returns {{
 *   configured: boolean, tz: string, openNow: boolean, todayKey: string,
 *   todayText: string, reopens: (string|null),
 *   week: Array<{key: string, short: string, label: string, text: string, open: boolean, today: boolean}>
 * }}
 */
export function hoursStrip(settings, now) {
    const hours = officeHours((settings || {}).office_hours);
    const local = clientLocalNow(hours.tz, now);
    const openNow = isOpenNow(hours, now);

    if (!hours.enabled) {
        return {
            configured: false,
            tz: hours.tz,
            openNow: true,
            todayKey: local.dayKey,
            todayText: 'Open 24 hours',
            reopens: null,
            week: DAYS.map((d) => ({
                key: d.key, short: d.short, label: d.label,
                text: 'Open', open: true, today: d.key === local.dayKey,
            })),
        };
    }

    const textFor = (key) => {
        const window = windowOf(hours.days[key]);

        // "Closed" covers a day switched off AND a day whose window is
        // unusable, because those are the same thing to a reader and the same
        // thing to the server.
        return window === null ? 'Closed' : fmt(window[0]) + '–' + fmt(window[1]);
    };

    const todayWindow = windowOf(hours.days[local.dayKey]);

    return {
        configured: true,
        tz: hours.tz,
        openNow,
        todayKey: local.dayKey,
        todayText: todayWindow === null ? 'Closed today' : 'Today ' + textFor(local.dayKey),
        reopens: nextOpenLabel(hours, now),
        week: DAYS.map((d) => ({
            key: d.key,
            short: d.short,
            label: d.label,
            text: textFor(d.key),
            open: windowOf(hours.days[d.key]) !== null,
            today: d.key === local.dayKey,
        })),
    };
}
